import { sessionFetch } from '../../utils/sessionFetch';
import { useState, useEffect } from 'react';
import { postAPI } from '../../utils/utils';
import './SensitiveDataGuard.css';

const REMEMBER_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 1 month

/**
 * A guard component that requires password re-entry to access sensitive data.
 * Shows a blurred overlay with a password prompt until the user authenticates.
 * 
 * The unlock state is stored in sessionStorage, so it persists during the session
 * but requires re-authentication when the browser is closed.
 * 
 * Users can optionally choose to remember for 1 month (stored in localStorage).
 */
function SensitiveDataGuard({ userId, userName, storageKey, children }) {
    const sessionKey = `sensitive_unlocked_${storageKey}`;
    const rememberKey = `sensitive_remember_${storageKey}`;
    
    // Check if already unlocked (session or long-term)
    const [isUnlocked, setIsUnlocked] = useState(() => {
        // First check localStorage for "remember for 1 month"
        const remembered = localStorage.getItem(rememberKey);
        if (remembered) {
            const rememberedAt = parseInt(remembered, 10);
            if (!isNaN(rememberedAt) && (Date.now() - rememberedAt) < REMEMBER_DURATION_MS) {
                return true;
            }
            // Expired, clean up
            localStorage.removeItem(rememberKey);
        }
        // Fall back to session check
        const stored = sessionStorage.getItem(sessionKey);
        return stored === 'true';
    });
    
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    // Clear error when password changes
    useEffect(() => {
        setError(null);
    }, [password]);

    const handleSubmit = (e) => {
        e?.preventDefault();
        
        if (!password || loading) return;
        
        setLoading(true);
        setError(null);

        sessionFetch(`${require('../../utils/utils').getServerEndpoint(require('../../utils/utils').COLLA)}/auth/verify_password`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': '453dabb4-7645-4626-a1bb-477dff3aa557'
            },
            body: JSON.stringify({ userId: parseInt(userId), password })
        })
            .then(response => response.json())
            .then(data => {
                setLoading(false);
                if (data.success) {
                    sessionStorage.setItem(sessionKey, 'true');
                    if (rememberMe) {
                        localStorage.setItem(rememberKey, Date.now().toString());
                    }
                    setIsUnlocked(true);
                } else {
                    setError('La contrasenya és incorrecta');
                }
            })
            .catch(() => {
                setLoading(false);
                setError('Error de connexió');
            });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    if (isUnlocked) {
        return children;
    }

    const buttonStyle = {
        width: '100%',
        padding: '14px 20px',
        background: (loading || !password) ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: (loading || !password) ? 'not-allowed' : 'pointer',
        opacity: (loading || !password) ? 0.6 : 1,
        transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
        boxSizing: 'border-box',
    };

    return (
        <div className="sensitive-guard-container">
            <div className="sensitive-guard-blurred">
                {children}
            </div>
            <div className="sensitive-guard-overlay">
                <div className="sensitive-guard-modal">
                    <div className="sensitive-guard-icon">🔒</div>
                    <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600', color: '#1a1a2e' }}>Dades sensibles</h2>
                    <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '14px', lineHeight: '1.5' }}>Introdueix la teva contrasenya per accedir a aquesta secció.</p>
                    
                    {error ? (
                        <div className="sensitive-guard-error">{error}</div>
                    ) : null}
                    
                    <form onSubmit={handleSubmit} className="sensitive-guard-form" autoComplete="off">
                        {/* Hidden username field so Chrome saves with a meaningful name */}
                        <input
                            type="text"
                            name="username"
                            value={userName || ''}
                            readOnly
                            autoComplete="username"
                            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                            tabIndex={-1}
                        />
                        <input
                            type="password"
                            name="password"
                            placeholder="Contrasenya"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            disabled={loading}
                            autoComplete="current-password"
                            className="sensitive-guard-input"
                        />
                        <label style={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: '10px',
                            fontSize: '13px',
                            color: '#555',
                            cursor: 'pointer',
                            marginBottom: '16px',
                            marginTop: '4px',
                            userSelect: 'none',
                            lineHeight: '1.4',
                            textAlign: 'left',
                            padding: '0',
                            margin: '8px 0 16px 0',
                            fontWeight: '400',
                            fontFamily: 'inherit',
                            background: 'transparent',
                            border: 'none',
                            width: '100%',
                        }}>
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                style={{ 
                                    width: '18px', 
                                    height: '18px',
                                    minWidth: '18px',
                                    minHeight: '18px',
                                    cursor: 'pointer',
                                    accentColor: '#3b82f6',
                                    margin: '0',
                                    padding: '0',
                                    flexShrink: '0',
                                    marginTop: '1px',
                                }}
                            />
                            <span style={{
                                flex: '1',
                                color: '#555',
                                fontSize: '13px',
                                lineHeight: '1.4',
                            }}>
                                No tornis a demanar això per dades sensibles en 1 mes
                            </span>
                        </label>
                        <button
                            type="submit"
                            disabled={loading || !password}
                            style={buttonStyle}
                        >
                            {loading ? 'Verificant...' : 'Desbloquejar'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default SensitiveDataGuard;
